<!--
  StatementPanel.vue
  콜봇 월 정산 · 정산 탭 맨 위 '마감 상태' 패널 — 화면 명세 2-4 를 목업 Detail.dc.html 모양으로(상태마다 같은 작은 패널).

  - 추정 중(estimating): 자동 마감 예정 한 줄(estimatingNotice)과 미리보기 한 줄("지금 마감하면 …", closePreviewText).
    지금 기준 경고는 접힌 '지금 기준 경고 n건' 안에 — 화면 이동 해결 방법만(기본 요율 화면·예외·대사 탭·안내).
  - 마감 중(closing): CLOSING_TEXT + [새로고침](reload).
  - 마감 실패(failed): 실패 사유(close_error · 시각) + 다음 자동 실행 안내(failedRetryText) + [지금 다시 실행](이 병원만
    POST close-runs, 끝난 달만 — 409 MONTH_NOT_ENDED, 503 BILLING_INDEXES_NOT_READY). 지금 기준 경고는 추정 중과 같이 접어 둔다.
  - 확정(confirmed): 초록 패널 '확정 · 시각 · 누가'(statementBadge) + 확정 배너 문장(수납 갈래 — confirmedBannerText) +
    읽기 전용 수납 상태(PaymentStatusLine). 경고는 회색 '참고 n건'(확정 때 확인한 차단 경고 · nonBlockingHint).
    [재수정]은 오른쪽 '이 달 확정' 카드(StatementConfirmCard)와 다른 탭의 헤더에 있다.
  - 검토 필요(review, 재수정 포함): '확정 전에 볼 것 n건' 번호 목록 — 차단 경고마다 이름 + 한 문장 + 해결 버튼 두 개까지
    (나머지 해결 방법·기다리기 안내는 '다른 방법' 안에). 해결 방법은 WARNING_META 그대로: 기본 요율 화면 링크 · 요율 탭 예외 모달 ·
    그 항목 금액 고치기(정산서 표) · [다시 계산] · 경고 확인(오른쪽 카드 체크) · 대사 탭. 비차단 경고는 회색 '참고 n건'.
    재수정 중·사람이 손댄 정산·마지막 자동 재계산 실패는 머리 아래 작은 회색 줄로.
  - 요금표 v70 이전 정산서(`statement.legacy`): 패널 맨 위 한 줄(+ 검토 필요면 [다시 계산], 확정이면 [재수정] 먼저 안내).
  - 모르는 상태: 상태 그대로 + "새로고침해 보세요".
  - 정산 대상 아닌 달(정산 시작일 전 — settlementStart.notBillableView): 다른 상태 패널 대신 회색 빈 상태 카드 하나 — 제목
    '정산 대상 아님' + emptyText + [시작일 바꾸기](canEditStart). 그 달에 지우지 못한 정산서가 남았으면 leftoverText 와
    [남은 정산서 지우기](canEditStart — 시작일 모달 cleanup) 또는 확정이면 [재수정]. 확정·저장·다시 계산·[지금 다시 실행]은 없다.
    canEditStart 는 셸의 provide('settlementCan') 로 받는다(기본 true).
  - 경고 코드는 본문에 쓰지 않는다 — 이름표에 title 로만.

  props: companyId · month · detail · api · reload · review(useStatementReview — 확인함 표시)
  emits: go-tab(tab, intent) · focus-line(key) · recalculate() · acknowledge(code) · open-start({ cleanup }) · open-reopen()
  exposes: showRecalcNote() — 검토 필요가 아닐 때 [다시 계산] 안내를 받으면 이유 한 줄 · focusWarnings() ·
           focusRecalculate() — [다시 계산]이 목록 앞 버튼에 있을 때(곁 카드는 그때 자기 버튼을 숨긴다)
  설명 툴팁(자동 마감 일정 · v70 이전 안내 · 설명의 뒷문장)은 정보 아이콘 버튼에 — 마우스·키보드 포커스로 열리고, 같은 글을
  aria-label 로도 읽는다.
  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 2-4", "요청 계약 4-1(수동 실행)·4-3·7"
-->
<template>
  <!-- 정산 대상 아닌 달 — 빈 상태 카드 -->
  <section
    v-if="notBillable.notBillable"
    ref="rootEl"
    class="st-state is-muted is-not-billable"
    :class="{ 'is-flash': flash }"
    :aria-labelledby="`${uid}-title`"
  >
    <p v-if="note" class="st-state__note" role="status">
      <InfoCircleOutlined aria-hidden="true" />
      <span>{{ note }}</span>
      <button type="button" class="st-state__note-close" aria-label="안내 닫기" @click="note = ''">닫기</button>
    </p>

    <header class="st-state__head">
      <div class="st-state__head-text">
        <h2 :id="`${uid}-title`" class="st-state__title">{{ NOT_BILLABLE_TITLE }}</h2>
        <span class="st-state__lead">{{ notBillable.emptyText }}</span>
      </div>
      <a-button v-if="canEditStart" @click="emit('open-start', { cleanup: false })">시작일 바꾸기</a-button>
    </header>

    <div v-if="notBillable.leftover" class="st-state__body">
      <div class="st-state__leftover" role="status">
        <span>{{ notBillable.leftoverText }}</span>
        <a-button
          v-if="notBillable.leftoverAction === 'cleanup' && canEditStart"
          size="small"
          @click="emit('open-start', { cleanup: true })"
        >
          남은 정산서 지우기
        </a-button>
        <a-button v-else-if="notBillable.leftoverAction === 'reopen'" size="small" @click="emit('open-reopen')">
          재수정
        </a-button>
      </div>
    </div>
  </section>

  <section
    v-else
    ref="rootEl"
    class="st-state"
    :class="[`is-${tone}`, { 'is-flash': flash }]"
    :aria-labelledby="`${uid}-title`"
  >
    <p v-if="note" class="st-state__note" role="status">
      <InfoCircleOutlined aria-hidden="true" />
      <span>{{ note }}</span>
      <button type="button" class="st-state__note-close" aria-label="안내 닫기" @click="note = ''">닫기</button>
    </p>

    <!-- Head: one title + one sentence -->
    <header class="st-state__head">
      <div class="st-state__head-text">
        <h2 :id="`${uid}-title`" class="st-state__title">{{ title }}</h2>
        <span v-if="lead" class="st-state__lead">
          {{ lead }}
          <a-tooltip v-if="scheduleText" :title="scheduleText" :trigger="TIP_TRIGGER">
            <button type="button" class="st-state__info" :aria-label="`자동 마감 일정: ${scheduleText}`">
              <InfoCircleOutlined aria-hidden="true" />
            </button>
          </a-tooltip>
        </span>
      </div>
      <a-button v-if="status === 'closing'" :loading="refreshing" @click="refresh">
        <ReloadOutlined v-if="!refreshing" />
        새로고침
      </a-button>
      <a-tooltip v-else-if="status === 'failed'" :title="rerunDisabledReason || undefined">
        <span class="st-state__btn-wrap">
          <a-button
            type="primary"
            class="yellow-button"
            :disabled="!!rerunDisabledReason"
            :loading="rerunning"
            @click="confirmRerun"
          >
            지금 다시 실행
          </a-button>
        </span>
      </a-tooltip>
    </header>

    <div v-if="hasBody" class="st-state__body">
      <!-- Small gray meta lines (each a distinct fact) -->
      <p v-for="line in metaLines" :key="line" class="st-state__meta">{{ line }}</p>

      <!-- v70-legacy statement -->
      <div v-if="legacy" class="st-state__legacy">
        <span>
          <strong>요금표 v70 이전 정산서</strong> · {{ legacyActionText }}
          <a-tooltip :title="LEGACY_STATEMENT_TEXT" :trigger="TIP_TRIGGER">
            <button type="button" class="st-state__info" :aria-label="`v70 이전 정산서 안내: ${LEGACY_STATEMENT_TEXT}`">
              <InfoCircleOutlined aria-hidden="true" />
            </button>
          </a-tooltip>
        </span>
        <a-button v-if="status === 'review'" size="small" data-action="recalculate" @click="emit('recalculate')">
          다시 계산
        </a-button>
      </div>

      <!-- Estimating: preview line -->
      <p v-if="status === 'estimating'" class="st-state__preview" :class="previewTone">{{ previewText }}</p>

      <!-- Confirmed: read-only payment line -->
      <PaymentStatusLine v-if="status === 'confirmed'" :detail="detail" />

      <!-- Review: numbered blocking list -->
      <ol v-if="status === 'review' && blocking.length" ref="warningsEl" class="st-todo">
        <li v-for="(w, index) in blocking" :key="w.code" class="st-todo__item">
          <span class="st-todo__num" aria-hidden="true">{{ index + 1 }}</span>
          <div class="st-todo__body">
            <span class="st-todo__title" :title="w.code">
              {{ warningText(w, { otherCost }) }}
              <span v-if="ackSet.has(w.code)" class="st-todo__acked">
                <CheckOutlined aria-hidden="true" />
                확인함
              </span>
            </span>
            <span class="st-todo__desc">
              {{ descParts(w).first }}
              <a-tooltip v-if="descParts(w).rest" :title="descParts(w).rest" :trigger="TIP_TRIGGER">
                <button type="button" class="st-state__info" :aria-label="`자세히: ${descParts(w).rest}`">
                  <InfoCircleOutlined aria-hidden="true" />
                </button>
              </a-tooltip>
            </span>
            <details v-if="plan(w).more.length || plan(w).texts.length" class="st-todo__more">
              <summary>다른 방법</summary>
              <div class="st-todo__more-body">
                <template v-for="res in plan(w).more" :key="res.key">
                  <router-link v-if="res.action === 'rates-page'" :to="ratesLocation" class="st-todo__link">
                    {{ res.label }} →
                  </router-link>
                  <a-button
                    v-else
                    size="small"
                    :disabled="res.action === 'acknowledge' && ackSet.has(w.code)"
                    :title="res.hint || undefined"
                    @click="onResolution(res, w)"
                  >
                    {{ resolutionLabel(res, w) }}
                  </a-button>
                </template>
                <span v-for="res in plan(w).texts" :key="res.key" class="st-todo__text">{{ resolutionText(res) }}</span>
              </div>
            </details>
          </div>
          <div v-if="plan(w).primary.length" class="st-todo__actions">
            <template v-for="res in plan(w).primary" :key="res.key">
              <router-link v-if="res.action === 'rates-page'" :to="ratesLocation" class="st-todo__link">
                {{ res.label }} →
              </router-link>
              <a-button
                v-else
                :title="res.hint || undefined"
                :data-action="res.action"
                @click="onResolution(res, w)"
              >
                {{ resolutionLabel(res, w) }}
              </a-button>
            </template>
          </div>
        </li>
      </ol>

      <!-- Estimating / failed: current warnings, collapsed (navigation actions only) -->
      <details v-if="showLater" ref="warningsEl" class="st-later">
        <summary>지금 기준 경고 {{ warnings.length }}건</summary>
        <ul class="st-later__list">
          <li v-for="w in warnings" :key="w.code" :class="{ 'is-blocking': w.blocking }">
            <span class="st-later__text" :title="w.code">{{ warningText(w, { otherCost }) }}</span>
            <span v-if="!w.blocking && nonBlockingHint(w, status)" class="st-later__hint">
              · {{ nonBlockingHint(w, status) }}
            </span>
            <span v-if="w.blocking && plan(w).all.length" class="st-later__actions">
              <template v-for="res in plan(w).all" :key="res.key">
                <router-link v-if="res.action === 'rates-page'" :to="ratesLocation" class="st-todo__link">
                  {{ res.label }} →
                </router-link>
                <span v-else-if="res.action === 'wait' || res.action === 'hint'" class="st-todo__text">
                  {{ resolutionText(res) }}
                </span>
                <a-button v-else size="small" :title="res.hint || undefined" @click="onResolution(res, w)">
                  {{ res.label }}
                </a-button>
              </template>
            </span>
          </li>
        </ul>
      </details>

      <!-- Review / confirmed: gray reference list -->
      <div v-if="referenceList.length" class="st-ref">
        <span class="st-ref__title">참고 {{ referenceList.length }}건</span>
        <ul class="st-ref__list">
          <li v-for="w in referenceList" :key="w.code">
            <span :title="w.code">{{ warningText(w, { otherCost }) }}</span>
            <span v-if="referenceHint(w)" class="st-ref__hint"> · {{ referenceHint(w) }}</span>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance, h, inject, nextTick, onBeforeUnmount, ref, unref, watch } from 'vue';
import { Modal, message } from 'ant-design-vue';
import { CheckOutlined, InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons-vue';

import PaymentStatusLine from '../PaymentStatusLine.vue';
import { formatKstDateTime, isNumber, monthLabel } from '../../service/settlementFormat';
import {
  BILLING_INDEXES_NOT_READY_TEXT,
  CLOSING_TEXT,
  LEGACY_STATEMENT_TEXT,
  SETTLEMENT_ROUTE_NAMES,
  closeErrorText,
  closePreviewText,
  closeRunDisabledReason,
  closeScheduleText,
  confirmedBannerText,
  currentCloseError,
  estimatingNotice,
  failedRetryText,
  isLegacyStatementDetail,
  legacyStatementActionText,
  nonBlockingHint,
  notFinalWaitHint,
  splitWarnings,
  statementBadge,
  statementStatusOf,
  todoResolutionPlan,
  warningMeta,
  warningText,
} from '../../service/settlementRules';
import { paymentScopeOf } from '../../service/settlementPayment';
import { notBillableView } from '../../service/settlementStart';
import { getCallApiErrorCode, parseCallApiError } from '@/views/Callbot/service/callApiError';

const props = defineProps({
  companyId: { type: String, required: true },
  month: { type: String, required: true },
  /** 정산 상세(요청 계약 2) */
  detail: { type: Object, required: true },
  api: { type: Object, required: true },
  /** 정산 상세를 다시 부른다 — 쓰기 뒤마다 */
  reload: { type: Function, required: true },
  /** useStatementReview() 의 반환값 — 체크한 차단 경고(확인함) */
  review: { type: Object, required: true },
});

const emit = defineEmits(['go-tab', 'focus-line', 'recalculate', 'acknowledge', 'open-start', 'open-reopen']);

/** 셸이 주는 권한 숨김 자리(5단계) — 값은 ref 또는 boolean. 셸 밖(시험)이면 둘 다 보인다 */
const can = inject('settlementCan', { canExport: true, canEditStart: true });
const canEditStart = computed(() => unref(can?.canEditStart) !== false);

const NOT_BILLABLE_TITLE = '정산 대상 아님';
const NOT_BILLABLE_RECALC_NOTE = '정산 시작일 전 달이라 [다시 계산]이 없습니다. 시작일을 바꾸면 이 달도 정산합니다.';

/** 정산 시작일 전 달 — 다른 상태 패널 대신 빈 상태 카드 */
const notBillable = computed(() => notBillableView(props.detail));

const uid = `st-state-${getCurrentInstance()?.uid ?? 0}`;

/** Explanation tooltips also open on keyboard focus (the info icons are buttons) */
const TIP_TRIGGER = ['hover', 'focus'];

/** Why there is no [다시 계산] when the shell's focus-recalculate intent lands outside review */
const RECALC_NOTES = {
  estimating: '아직 마감 전이라 [다시 계산]이 없습니다. 금액·경고는 열 때마다 지금 요율·사용량으로 계산됩니다.',
  closing: '자동 마감이 계산하는 중이라 지금은 다시 계산할 수 없습니다. 잠시 후 새로고침하세요.',
  confirmed: '확정된 달은 금액이 바뀌지 않습니다. 반영하려면 [재수정]한 뒤 [다시 계산]합니다.',
  failed: '마감에 실패한 달은 [지금 다시 실행]으로 다시 마감합니다.',
};

const arr = (v) => (Array.isArray(v) ? v : []);
const num = (v) => (isNumber(v) ? v : null);

const status = computed(() => statementStatusOf(props.detail));
const statement = computed(() => props.detail.statement ?? null);
const legacy = computed(() => isLegacyStatementDetail(props.detail));
const legacyActionText = computed(() => legacyStatementActionText(status.value));
const warnings = computed(() => arr(props.detail.warnings));
const split = computed(() => splitWarnings(warnings.value));
const blocking = computed(() => split.value.blocking);
const nonBlocking = computed(() => split.value.nonBlocking);
const ackSet = computed(() => props.review.ackSet.value);
const companyName = computed(() => props.detail.company_name || props.companyId);
const ratesLocation = computed(() => ({ name: SETTLEMENT_ROUTE_NAMES.rates, query: { month: props.month } }));

/** Panel colour: review orange · confirmed green · closing blue · failed red · estimating (and unknown) gray */
const TONES = { review: 'review', confirmed: 'ok', closing: 'progress', failed: 'danger' };
const tone = computed(() => TONES[status.value] ?? 'muted');

// ---------------------------------------------------------------------------
// Head — one title, one sentence
// ---------------------------------------------------------------------------

const failedReason = computed(() => {
  const err = statement.value?.close_error ?? null;
  const at = num(err?.at);
  return at === null ? closeErrorText(err) : `${closeErrorText(err)} · ${formatKstDateTime(at)}`;
});

const title = computed(() => {
  switch (status.value) {
    case 'estimating':
      return '추정 중 · 마감 전';
    case 'closing':
      return '마감 중';
    case 'failed':
      return '마감 실패';
    case 'confirmed':
      return statementBadge(statement.value).label;
    case 'review': {
      const head = statement.value?.reopened ? '재수정 중 · ' : '';
      return blocking.value.length ? `${head}확정 전에 볼 것 ${blocking.value.length}건` : `${head}남은 차단 경고 없음`;
    }
    default:
      return String(status.value);
  }
});

const lead = computed(() => {
  switch (status.value) {
    case 'estimating':
      return estimatingNotice(props.detail, props.month);
    case 'closing':
      return CLOSING_TEXT;
    case 'failed':
      return failedReason.value;
    case 'confirmed':
      return confirmedBannerText(paymentScopeOf(props.detail));
    case 'review':
      if (statement.value?.reopened) return '다시 확정할 때까지 직전 확정 금액이 이 달 확정 정산 금액으로 남습니다.';
      return blocking.value.length
        ? '자동 마감이 아래 이유로 확정하지 않고 멈췄습니다.'
        : '한 번 멈춘 정산은 자동으로 확정하지 않습니다. 금액을 확인하고 확정합니다.';
    default:
      return '화면이 모르는 마감 상태입니다. 새로고침해 보세요.';
  }
});

/** Distinct facts under the head (small gray) */
const metaLines = computed(() => {
  const out = [];
  if (status.value === 'failed') out.push(failedRetryText(props.month, Date.now(), props.detail.auto_close ?? null));
  if (status.value === 'review') {
    if (statement.value?.human_touched && !statement.value?.reopened) {
      out.push('사람이 손댄 정산이라 자동 마감이 다시 계산하지 않습니다.');
    }
    // A failed automatic recalculation left on a review statement (status stays review — reply table 2)
    const err = currentCloseError(statement.value);
    if (err) {
      const at = num(err.at);
      out.push(`마지막 자동 재계산이 실패했습니다: ${closeErrorText(err)}${at === null ? '' : ` · ${formatKstDateTime(at)}`}`);
    }
  }
  return out;
});

/** Auto-close schedule (close day · hour, or not a target month) — tooltip on the head */
const scheduleText = computed(() => closeScheduleText(props.detail.auto_close ?? null));

const previewText = computed(() => closePreviewText(warnings.value));
const previewTone = computed(() => ({
  'is-danger': blocking.value.some((w) => w?.code !== 'WEB_USAGE_UNVERIFIED'),
  'is-ok': !warnings.value.length,
}));

const showLater = computed(() => (status.value === 'estimating' || status.value === 'failed') && warnings.value.length > 0);

// ---------------------------------------------------------------------------
// Warnings and resolutions
// ---------------------------------------------------------------------------

/** Cold-transfer text without a count gets ' · 기타 원가 n원' (warningText otherCost) */
const otherCost = computed(() => num(props.detail.cost?.split?.other));

/** NOT_FINAL_CALLS "통화가 끝나길 기다리기" hint — "매일 자동으로" only when auto close really recalculates */
const waitHint = computed(() => notFinalWaitHint(props.detail, props.month));

/** Description → first sentence visible, the rest in a tooltip */
function descParts(w) {
  const text = warningMeta(w.code).description ?? '';
  const m = /^(.+?[다요]\.)\s+([\s\S]+)$/.exec(text);
  return m ? { first: m[1], rest: m[2] } : { first: text, rest: '' };
}

/**
 * Resolutions of one warning for the compact list: at most two primary buttons (never the acknowledge — the aside
 * checkbox does that), the other buttons and the text-only ones under '다른 방법'. Outside review only navigation.
 * The aside card reads the same plan (todoResolutionPlan) to avoid a second [다시 계산].
 */
function plan(w) {
  return todoResolutionPlan(w, status.value);
}

function resolutionLabel(res, w) {
  if (res.action === 'acknowledge' && ackSet.value.has(w.code)) return '경고 확인함';
  return res.label;
}

function resolutionText(res) {
  if (res.action === 'wait') return `${res.label} · ${res.hint || waitHint.value}`;
  return res.label;
}

/** Gray '참고' list — review: non-blocking; confirmed: every warning (blocking ones were checked at confirm time) */
const referenceList = computed(() => {
  if (status.value === 'review') return nonBlocking.value;
  if (status.value === 'confirmed') return warnings.value;
  return [];
});

function referenceHint(w) {
  if (status.value === 'confirmed' && w.blocking) return '확정 때 확인한 차단 경고';
  return nonBlockingHint(w, status.value);
}

const hasBody = computed(
  () =>
    metaLines.value.length > 0 ||
    legacy.value ||
    status.value === 'estimating' ||
    status.value === 'confirmed' ||
    (status.value === 'review' && blocking.value.length > 0) ||
    showLater.value ||
    referenceList.value.length > 0
);

function onResolution(res, w) {
  switch (res.action) {
    case 'override-modal':
      emit('go-tab', 'rates', { type: 'open-override-modal' });
      break;
    case 'recon-missing-ids':
      emit('go-tab', 'recon', { type: 'check-missing-ids' });
      break;
    case 'recon-count':
      emit('go-tab', 'recon', { type: 'check-count' });
      break;
    case 'line-input':
      emit('focus-line', res.line ?? 'base');
      break;
    case 'recalculate':
      emit('recalculate');
      break;
    case 'acknowledge':
      emit('acknowledge', w.code);
      break;
    default:
      break;
  }
}

// ---------------------------------------------------------------------------
// Writes — refresh while closing, rerun after a failure
// ---------------------------------------------------------------------------

const refreshing = ref(false);
const rerunning = ref(false);
const rerunDisabledReason = computed(() => closeRunDisabledReason(props.month));

async function refresh() {
  if (refreshing.value) return;
  refreshing.value = true;
  try {
    await props.reload();
  } finally {
    refreshing.value = false;
  }
}

function confirmRerun() {
  if (rerunning.value || rerunDisabledReason.value) return;
  Modal.confirm({
    title: '지금 다시 실행',
    content: h('div', [
      h('p', `${monthLabel(props.month)} ${companyName.value} 마감을 지금 다시 실행합니다.`),
      h(
        'p',
        '차단 경고가 없으면 바로 확정되어 그 금액이 확정 정산 금액이 되고, 있으면 검토 필요로 멈춥니다. 실행은 비동기라 끝나면 새로고침으로 결과를 봅니다.'
      ),
    ]),
    okText: '다시 실행',
    cancelText: '취소',
    // rerun never rejects — the OK button keeps loading until it ends
    onOk: () => rerun(),
  });
}

async function rerun() {
  rerunning.value = true;
  try {
    await props.api.startCloseRun({ month: props.month, companyId: props.companyId });
    message.success('이 병원 마감을 다시 실행했습니다. 잠시 후 새로고침해 결과를 확인하세요.');
    await props.reload();
  } catch (err) {
    const code = getCallApiErrorCode(err);
    if (code === 'MONTH_NOT_ENDED') {
      message.warning('아직 끝나지 않은 달은 마감을 실행할 수 없습니다. 달이 끝난 뒤 다시 실행하세요.');
    } else if (code === 'BILLING_INDEXES_NOT_READY') {
      message.warning(BILLING_INDEXES_NOT_READY_TEXT);
    } else {
      message.error(parseCallApiError(err, '마감을 다시 실행하지 못했습니다.'));
    }
  } finally {
    rerunning.value = false;
  }
}

// ---------------------------------------------------------------------------
// Moves from the shell / other tabs
// ---------------------------------------------------------------------------

/** One line telling why [다시 계산] is not here */
const note = ref('');
const rootEl = ref(null);
const warningsEl = ref(null);
const flash = ref(false);
let flashTimer = null;

function flashRoot() {
  rootEl.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  flash.value = true;
  clearTimeout(flashTimer);
  flashTimer = setTimeout(() => {
    flash.value = false;
  }, 2200);
}

async function showRecalcNote() {
  note.value = notBillable.value.notBillable ? NOT_BILLABLE_RECALC_NOTE : RECALC_NOTES[status.value] ?? '';
  await nextTick();
  flashRoot();
}

/**
 * Intent focus-recalculate when [다시 계산] lives on a todo item (the aside card then hides its own) — flash the panel
 * and focus that button
 */
async function focusRecalculate() {
  await nextTick();
  flashRoot();
  rootEl.value?.querySelector?.('[data-action="recalculate"]')?.focus({ preventScroll: true });
}

async function focusWarnings() {
  await nextTick();
  // Open the collapsed list so the warnings are visible
  const el = warningsEl.value;
  if (el && el.tagName === 'DETAILS') el.open = true;
  flashRoot();
}

// A note written for the previous status goes away with it
watch([status, () => notBillable.value.notBillable], () => {
  note.value = '';
});

onBeforeUnmount(() => clearTimeout(flashTimer));

defineExpose({ showRecalcNote, focusWarnings, focusRecalculate });
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

@include yellow-button;

// Review title / number colour — the mockup's dark orange
$review-strong: #8a4500;

.st-state {
  overflow: hidden;
  border: 1px solid $st-border-strong;
  border-radius: 12px;
  background: #fff;
  color: $st-text;
  transition: box-shadow 0.3s ease;

  &.is-flash {
    box-shadow: 0 0 0 2px $st-accent-border;
  }

  &__note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 0;
    padding: 8px 20px;
    border-bottom: 1px solid $st-accent-border;
    background: $st-accent-bg;
    font-size: 12.5px;
    color: $st-text-secondary;
  }

  &__note-close {
    margin-left: auto;
    height: auto; // override the global button { height: 40px }
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    font-size: 12px;
    color: $st-muted;
    cursor: pointer;
    white-space: nowrap;
  }

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px 16px;
    padding: 14px 20px;
    background: $st-bg;
  }

  &__head-text {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 10px;
    min-width: 0;
  }

  &__title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  &__lead {
    font-size: 13px;
    color: $st-text-secondary;
  }

  &__btn-wrap {
    display: inline-block;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 20px 14px;
    border-top: 1px solid $st-border;
  }

  &__meta {
    margin: 0;
    font-size: 12.5px;
    color: $st-muted;
  }

  // 정산 대상 아닌 달에 남은 정산서 — 버튼 하나와 함께
  &__leftover {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 6px 12px;
    padding: 8px 12px;
    border: 1px solid $st-warning-border;
    border-radius: 8px;
    background: $st-warning-bg;
    color: $st-text;
    font-size: 13px;
  }

  &__legacy {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 6px 12px;
    padding: 8px 12px;
    border-radius: 8px;
    background: $st-warning-bg;
    font-size: 13px;
    color: $st-text-secondary;
  }

  &__info {
    @include st-info-btn;
  }

  &__preview {
    margin: 0;
    font-size: 13.5px;
    font-weight: 600;
    color: $st-text-secondary;

    &.is-danger {
      color: $st-warning;
    }

    &.is-ok {
      color: $st-ok;
    }
  }

  // Status tones — the head carries the colour
  &.is-review {
    border-color: $st-warning-border;

    .st-state__head {
      background: $st-warning-bg;
    }

    .st-state__body {
      border-top-color: $st-warning-border;
    }

    .st-state__title {
      color: $review-strong;
    }
  }

  &.is-ok {
    border-color: $st-ok-border;

    .st-state__head {
      background: $st-ok-bg;
    }

    .st-state__body {
      border-top-color: $st-ok-border;
    }

    .st-state__title {
      color: $st-ok;
    }
  }

  &.is-progress {
    border-color: $st-accent-border;

    .st-state__head {
      background: $st-accent-bg;
    }

    .st-state__body {
      border-top-color: $st-accent-border;
    }

    .st-state__title {
      color: $st-accent;
    }
  }

  &.is-danger {
    border-color: $st-danger-border;

    .st-state__head {
      background: $st-danger-bg;
    }

    .st-state__body {
      border-top-color: $st-danger-border;
    }

    .st-state__title {
      color: $st-danger;
    }
  }
}

// Review — numbered "확정 전에 볼 것"
.st-todo {
  display: flex;
  flex-direction: column;
  margin: 0 -20px;
  padding: 0;
  list-style: none;

  &__item {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 16px;
    padding: 12px 20px;
    border-bottom: 1px solid $st-border;

    &:first-child {
      padding-top: 2px;
    }

    &:last-child {
      border-bottom: 0;
      padding-bottom: 2px;
    }
  }

  &__num {
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background: $review-strong;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  &__body {
    display: flex;
    flex: 1 1 320px;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  &__title {
    font-size: 14.5px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  &__acked {
    @include st-pill($st-ok, $st-ok-bg, $st-ok-border);
    margin-left: 6px;
    vertical-align: 1px;
  }

  &__desc {
    font-size: 13px;
    line-height: 1.6;
    color: $st-text-secondary;
  }

  &__more {
    margin-top: 2px;
    font-size: 12.5px;

    summary {
      color: $st-muted;
      cursor: pointer;
    }
  }

  &__more-body {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    margin-top: 6px;
  }

  &__text {
    font-size: 12.5px;
    color: $st-muted;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  &__link {
    font-size: 13px;
    font-weight: 600;
  }
}

// Estimating / failed — collapsed current warnings
.st-later {
  font-size: 13px;

  summary {
    font-weight: 600;
    color: $st-text-secondary;
    cursor: pointer;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 8px 0 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px 8px;
      color: $st-muted;

      &.is-blocking .st-later__text {
        font-weight: 600;
        color: $st-warning;
      }
    }
  }

  &__hint {
    font-size: 12.5px;
  }

  &__actions {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
  }
}

// Review / confirmed — gray reference list
.st-ref {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12.5px;
  color: $st-muted;

  &__title {
    font-weight: 600;
    color: $st-text-secondary;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__hint {
    color: $st-text-secondary;
  }
}
</style>
